import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import type { Company } from 'context/authentication/Company.types'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { COMPANIES_QUERY_KEY, deleteCompany } from './CompanyManager.service'

interface DeleteCompanyDialogPayload {
  readonly company: Company
}

interface DeleteCompanyDialogProps {
  readonly payload: DeleteCompanyDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const DeleteCompanyDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: DeleteCompanyDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, formState, setError } = useForm()
  const queryClient = useQueryClient()
  const deleteCompanyMutation = useMutation({
    mutationFn: deleteCompany,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [COMPANIES_QUERY_KEY] }),
  })
  const [deletionConfirmed, setDeletionConfirmed] = useState(false)

  useEffect(() => {
    setDeletionConfirmed(false)
  }, [payload])

  const onSubmit: SubmitHandler<object> = async (): Promise<void> => {
    await handleFormSubmission(() => deleteCompanyMutation.mutateAsync(payload.company.cid), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: onAction,
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.company.displayName} — ${t('screens.shared.company_manager.delete_company.delete_company')}`}
      actions={
        <Button
          type="submit"
          color="error"
          variant="contained"
          loading={formState.isSubmitting}
          disabled={!deletionConfirmed}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.shared.company_manager.card.actions.delete')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="xs"
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Typography>
        {t('screens.shared.company_manager.delete_company.message')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            value={deletionConfirmed}
            checked={deletionConfirmed}
            onChange={(): void => setDeletionConfirmed(!deletionConfirmed)}
          />
        }
        label={t('shared.manager.deletion_confirm_checkbox')}
      />
    </KNDialog>
  )
}

export default DeleteCompanyDialog
